import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { Loc } from '../locales/registry.js';
import { parseCmdAllows } from '../core/CmdAllows.js';
import { DomHelpers } from '../core/DomHelpers.js';

// widgets/CmdPanelWidget.ts
// Универсальная панель команд для таблиц/форм/коллекций.
/**
 * Простая шина команд: key → handler.
 * Панель ничего не вызывает напрямую, только emit(key).
 */
class CmdBus {
    constructor() {
        /** Таблица: ключ команды → { handler, confirm? } */
        this.map = new Map();
    }
    /**
     * Подписаться на команду.
     * @param id         Ключ команды (например, "save", "delete" и т.д.)
     * @param handler    Обработчик, который будет вызван при emit()
     * @param confirm    Параметры предупреждения о действии.
     */
    on(id, handler, confirm) {
        this.map.set(id, { handler, confirm });
    }
    /** Отписать обработчик. */
    off(id) { this.map.delete(id); }
    /** Проверить наличие обработчика. */
    has(id) { return this.map.has(id); }
    /** Вызвать обработчик (если есть). */
    emit(id, source) {
        const rec = this.map.get(id);
        if (!rec) {
            console.error(`CmdBus.emit: no handler for "${id}"`);
            return;
        }
        const run = () => rec.handler({ id, source });
        if (rec.confirm?.text) {
            ImSys.alertConfirm(rec.confirm.title ?? Loc.confirmation, rec.confirm.text, run);
        }
        else {
            run();
        }
    }
}
/**
 * CmdPanelWidget — панель команд.
 * Отвечает только за отрисовку, состояние доступности и эмит команд в CmdBus.
 */
class CmdPanelWidget extends Widget {
    constructor() {
        super(...arguments);
        // --------------------------- Поля экземпляра --------------------------------
        /** Текущее агрегированное состояние панели. */
        this.state = { mode: "view", selectedCount: 0, hasCurrent: false };
        /** Строка прав/разрешений. */
        this.allows = "";
        /** Включён ли «режим формы» (тумблер bt-formmode). */
        this.formModeOn = false;
    }
    /** Узнать, включён ли режим формы. */
    isFormModeOn() {
        return this.formModeOn;
    }
    // ----------------------------- Жизненный цикл -------------------------------
    /** Инициализация: сборка UI, применение allows, привязка хэндлеров. */
    _create(options) {
        this.options = options;
        this.allows = options.allow ?? "";
        this.ui = this.buildUI(this.element, options);
        if (this.options.filterButton === false) {
            this.hide("openFilter", true);
        }
        // перенос «кастомов» до применения allows/state
        this._adoptCustomItems();
        this.applyAllows();
        this.updateUI();
        this.attachHandlers();
    }
    /** Перемещает элементы из customItemsSource в контейнер панели и «гидрирует» кнопки. */
    _adoptCustomItems() {
        const srcEl = this.options.customItemsSource;
        if (!srcEl)
            return;
        // Кастомный toolstrip может разместить штатный счётчик выделения
        // внутри смысловой кнопки. Он становится основным после переноса.
        const adoptedCheckedCount = srcEl.querySelector(".im-checked-count");
        // целевой слот — контейнер панели
        const slot = this.ui.container;
        // Добавляем сплиттер только если:
        //  - в слоте уже есть КНОПКИ (а не пусто),
        //  - перед пагинацией (или в конце) нет сплиттера,
        //  - первый переносимый элемент не splitter
        const hasButtonsInSlot = !!slot.querySelector(".im-cmdbutton");
        const pagination = slot.querySelector(".im-toolstrip__pagination");
        let lastBeforePagination = null;
        if (pagination) {
            lastBeforePagination = pagination.previousElementSibling;
        }
        else {
            lastBeforePagination = slot.lastElementChild;
        }
        const lastIsSplitter = !!lastBeforePagination && lastBeforePagination.classList.contains("splitter");
        const firstMovedIsSplitter = srcEl.firstElementChild?.classList.contains("splitter");
        if (hasButtonsInSlot && !lastIsSplitter && !firstMovedIsSplitter) {
            if (pagination) {
                slot.insertBefore(this.splitter(), pagination);
            }
            else {
                slot.appendChild(this.splitter());
            }
        }
        Array.from(srcEl.children).forEach(ch => {
            if (pagination) {
                slot.insertBefore(ch, pagination);
            }
            else {
                slot.appendChild(ch);
            }
            // NOTE: В замен el.show(), но возможно не во всех случаях сработает?
            ch.style.removeProperty("display");
        });
        if (adoptedCheckedCount
            && adoptedCheckedCount !== this.ui.checkedCount) {
            this.ui.checkedCount.remove();
            this.ui.checkedCount = adoptedCheckedCount;
        }
        // подключаем кастомные кнопки на шину 'custom'
        // NOTE: Сарая версия подключения не использовалась похоже, обработка шла через виджет меню
        // реализовывать не нужно
        // удаляем пустую обёртку (как в легаси)
        if (srcEl !== slot && srcEl.childElementCount === 0) {
            try {
                srcEl.remove();
            }
            catch { /* noop */ }
        }
    }
    /** Очистка ресурсов: снять хэндлеры. DOM остаётся, т.к. это часть host. */
    _onDestroy() {
        this.detachHandlers();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.ui = undefined;
    }
    // ----------------------------- Публичный API --------------------------------
    /** Установить строку прав и немедленно применить к UI. */
    setAllows(allows) {
        this.allows = allows ?? "";
        this.applyAllows();
        this.updateUI();
    }
    setState(s) {
        const prev = this.state;
        // 1) Смерджим, но не трогаем filterCount, если он не передан
        const next = {
            ...prev,
            ...s,
            filterCount: (s.filterCount === undefined ? prev.filterCount : s.filterCount)
        };
        // 2) Если filterOn явно НЕ задали, но есть активные фильтры — считаем фильтр включённым
        const cnt = next.filterCount ?? 0;
        if (s.filterOn === undefined && cnt > 0) {
            next.filterOn = true;
        }
        // ВАЖНО: если cnt === 0 — НИЧЕГО не делаем, статус filterOn остаётся как был,
        // т.к. визуальное закрытие панели ≠ выключение фильтра по бизнес-логике.
        this.state = next;
        this.updateUI();
    }
    /**
     *  Обновить состояние с учтетом количества выбранных строк
     * (влияет на enable multirow-кнопок).
     */
    setSelection(selectedCount) {
        this.state.selectedCount = selectedCount ?? 0;
        this.updateUI();
    }
    /** Переключить индикатор загрузки (режим loading). */
    setBusy(isBusy) {
        if (isBusy)
            this.state.mode = "loading";
        else if (this.state.mode === "loading")
            this.state.mode = "view";
        this.updateUI();
    }
    /** Получить текущую страницу (для обработчиков из cmdBus). */
    getPage() { return this.state.page ?? 1; }
    /** Получить значение «строк на странице», если селектор включён. */
    getMaxRows() {
        return this.ui.pagination?.maxRowsSel ? parseInt(this.ui.pagination.maxRowsSel.value, 10) : undefined;
    }
    /** Узнать, включён ли фильтр (для обработчиков из cmdBus). */
    isFilterOn() { return !!this.state.filterOn; }
    /** Количество активных фильтров */
    get filterCount() {
        return this.state.filterCount || 0;
    }
    // ----------------------------- Построение UI --------------------------------
    /** Хелпер: первичная проверка разрешений для команды. */
    isInitiallyAllowed(id) {
        // базовые флаги
        const allow = parseCmdAllows(this.allows);
        const groupsOk = this.options.supportsGroups === true;
        switch (id) {
            case "reload": return !allow.hideReload;
            case "openFilter": return !allow.hideFilter; // "closeFilter" появится/переключится из "openFilter"
            case "save":
            case "cancel": return true; // зависят от режима, управления в updateUI
            case "saveClose":
                return !this.options.noSaveClose;
            case "edit": return allow.edit || allow.rowEdit;
            case "insert":
            case "insertCopy": return allow.insert;
            case "delete": return allow.del;
            case "delMark": return allow.delMark;
            case "editGroup":
            case "insertGroup": return groupsOk && allow.insertGroup;
            case "changeGroup": return groupsOk && allow.edit || allow.rowEdit;
            case "deleteFolder": return groupsOk && allow.del;
            case "rowsMoveUp":
            case "rowsMoveDown": return allow.move;
            case "copy": return allow.copy;
            case "cut": return allow.cut;
            case "paste": return allow.paste;
            case "formMode": return !!(allow.formEdit && allow.rowEdit);
            default: return true;
        }
    }
    /**
     * Создаёт кнопки разрешённые и имеющие handler.
     *
     * NOTE: CmdPanelWidget отрисовывает кнопки ТОЛЬКО для команд,
     * на которые обработчик был подписан в момент инициализации.
     * Поздняя подписка через cmdBus.on(...) не добавит кнопку автоматически.
     */
    buildUI(host, opts) {
        const buttons = getStandardButtons();
        // 1) ищем уже существующую панель
        const existingRoot = host.classList.contains("im-toolstrip")
            ? host
            : host.querySelector(".im-toolstrip");
        const root = existingRoot ?? document.createElement("div");
        if (!existingRoot) {
            root.className = "im-toolstrip";
            host.appendChild(root);
        }
        // 2) контейнер
        let container = root.querySelector(".im-toolstrip__container");
        if (!container) {
            container = document.createElement("div");
            container.className = "im-toolstrip__container";
            // ⬇️ ВАЖНО: вставляем контейнер ПЕРЕД существующим содержимым root,
            // чтобы кнопки шли перед .menu-bt и прочими ранее добавленными узлами.
            root.insertBefore(container, root.firstChild);
            // ...и переносим в него всё остальное (кроме лоадера)
            Array.from(root.children)
                .filter(el => el !== container && !el.classList.contains("loading-container"))
                .forEach(el => container.appendChild(el));
        }
        // ⬇️ ЗАПОМИНАЕМ первый существующий узел К ДОБАВЛЕНИЯ любых новых элементов
        // Если контейнер уже был и в нём есть контент — будем вставлять "дорисованные" кнопки перед ним.
        const preExistingFirstNode = container.firstChild;
        // 3) checked-count
        const checkedCount = root.querySelector(".im-checked-count") ??
            (() => {
                const span = document.createElement("span");
                span.className = "im-checked-count";
                container.appendChild(span);
                return span;
            })();
        // 4) гидратация существующих кнопок по dataset.cmd
        const btns = new Map();
        root.querySelectorAll(".im-cmdbutton").forEach((b) => {
            const id = b.dataset.cmd?.trim();
            if (!id)
                return; // кнопка декоративная — пропускаем
            if (id !== "formMode" && !this.options.cmdBus.has(id))
                return; // нет handler — скрываем смысловую кнопку
            if (!this.isInitiallyAllowed(id))
                return; // запрещено правами — игнорим
            // Подставляем локаль и aria, если их нет
            const spec = buttons.find(s => String(s.id) === id);
            if (spec?.title) {
                if (!b.title)
                    b.title = spec.title;
                if (!b.getAttribute("aria-label"))
                    b.setAttribute("aria-label", spec.title);
            }
            // нормализуем и навешиваем обработчик
            b.addEventListener("click", (ev) => this.onClick(ev));
            if (id === "openFilter" || id === "closeFilter") {
                btns.set("openFilter", b);
                btns.set("closeFilter", b);
            }
            else {
                btns.set(id, b);
            }
        });
        // 5) дорисовать недостающие (если разрешено)
        const shouldAddMissing = opts.addMissingButtons !== false;
        // вспомогательная вставка с учётом якоря
        const insertBeforeAnchor = (node) => {
            if (preExistingFirstNode)
                container.insertBefore(node, preExistingFirstNode);
            else
                container.appendChild(node);
        };
        // разделители фолбэчно как раньше
        let addedEditGroupSplitter = false;
        let addedDeleteGroupSplitter = false;
        let addedFolderMoveSplitter = false;
        let addedClipboardSplitter = false;
        if (shouldAddMissing) {
            for (const spec of buttons) {
                const id = String(spec.id);
                if (btns.has(id))
                    continue; // уже есть
                if (id !== "formMode" && !this.options.cmdBus.has(id))
                    continue; // нет handler — не выводим
                if (id === "openFilter" && opts.filterButton === false)
                    continue;
                if (!this.isInitiallyAllowed(id))
                    continue;
                if (btns.size > 0) {
                    if (!addedEditGroupSplitter && ["cancel", "save", "saveClose", "edit"].includes(id)) {
                        insertBeforeAnchor(this.splitter());
                        addedEditGroupSplitter = true;
                    }
                    if (!addedDeleteGroupSplitter && ["delMark", "delete"].includes(id)) {
                        insertBeforeAnchor(this.splitter());
                        addedDeleteGroupSplitter = true;
                    }
                    if (!addedFolderMoveSplitter && ["editGroup", "rowsMoveUp"].includes(id)) {
                        insertBeforeAnchor(this.splitter());
                        addedFolderMoveSplitter = true;
                    }
                    if (!addedClipboardSplitter && ["cut", "copy", "paste"].includes(id)) {
                        insertBeforeAnchor(this.splitter());
                        addedClipboardSplitter = true;
                    }
                }
                this.addBtn(container, btns, { id, icon: spec.icon, title: spec.title }, preExistingFirstNode);
            }
        }
        // 6) пагинация
        let pagination;
        if (opts.pagination) {
            const wrap = root.querySelector(".im-toolstrip__pagination");
            if (wrap) {
                const prev = wrap.querySelector(".bt-prev-page") || undefined;
                const next = wrap.querySelector(".bt-next-page") || undefined;
                const pageInput = wrap.querySelector(".im-pagenumbox") || undefined;
                const maxRowsSel = wrap.querySelector(".im-maxrowsmax") || undefined;
                prev?.addEventListener("click", () => this.emitNav(-1));
                next?.addEventListener("click", () => this.emitNav(1));
                pageInput?.addEventListener("change", () => {
                    const val = Math.max(1, parseInt(pageInput.value || "1", 10));
                    pageInput.value = String(val);
                    this.emitPage(val);
                });
                maxRowsSel?.addEventListener("change", () => this.emitSetMaxRows());
                pagination = { wrap, pageInput, prev, next, maxRowsSel };
            }
            else {
                pagination = this.addPagination(container, opts.maxRowsSelector);
            }
        }
        const fmBtn = btns.get("formMode");
        this.formModeOn = fmBtn ? fmBtn.classList.contains("bt-formmode") : false;
        this.filterBtn = btns.get("openFilter") ?? undefined;
        return { root, container, btns, checkedCount, pagination };
    }
    /** Показать/обновить счётчик выбранных фильтров на кнопке "Фильтр" в тулбаре. */
    setFilterCount(count) {
        this.setState({ filterCount: Math.max(0, count) });
    }
    /** Создать кнопку и привязать клики к onClick. */
    addBtn(container, btns, spec, placeBefore) {
        const btn = document.createElement("button");
        btn.type = "button";
        // ⬇️ для formMode ставим иконку OFF, иначе — как задано
        const baseIcon = spec.icon ?? "";
        const iconClass = String(spec.id) === "formMode"
            ? (baseIcon.includes("bt-formmode") ? "bt-formmodeoff" : "bt-formmodeoff")
            : baseIcon;
        btn.className = `im-cmdbutton ${iconClass}`;
        btn.dataset.cmd = String(spec.id);
        btn.title = spec.title ?? "";
        btn.setAttribute("aria-label", spec.title ?? String(spec.id));
        btn.hidden = !!spec.hidden;
        btn.disabled = !!spec.disabled;
        btn.addEventListener("click", (ev) => this.onClick(ev));
        if (placeBefore)
            container.insertBefore(btn, placeBefore);
        else
            container.appendChild(btn);
        btns.set(String(spec.id), btn);
        return btn;
    }
    /** Разделитель кнопок. */
    splitter() {
        const s = document.createElement("span");
        s.className = "splitter";
        return s;
    }
    /** Блок пагинации и его обработчики (navPrev/navNext/goPage/setMaxRows). */
    addPagination(container, options) {
        const wrap = document.createElement("div");
        wrap.className = "im-toolstrip__pagination";
        const prev = document.createElement("button");
        prev.type = "button";
        prev.className = "im-cmdbutton bt-prev-page";
        prev.addEventListener("click", () => this.emitNav(-1));
        const pageInput = document.createElement("input");
        pageInput.className = "im-pagenumbox";
        pageInput.type = "text";
        pageInput.addEventListener("change", () => {
            const val = Math.max(1, parseInt(pageInput.value || "1", 10));
            pageInput.value = String(val);
            this.emitPage(val);
        });
        const next = document.createElement("button");
        next.type = "button";
        next.className = "im-cmdbutton bt-next-page";
        next.addEventListener("click", () => this.emitNav(1));
        wrap.appendChild(prev);
        wrap.appendChild(pageInput);
        wrap.appendChild(next);
        if (options?.length) {
            const sel = document.createElement("select");
            sel.className = "im-maxrowsmax";
            for (const n of options) {
                const opt = document.createElement("option");
                opt.value = String(n);
                opt.textContent = String(n);
                sel.appendChild(opt);
            }
            sel.value = String(options[0]);
            sel.addEventListener("change", () => this.emitSetMaxRows());
            wrap.appendChild(sel);
            container.appendChild(wrap);
            return { wrap, pageInput, prev, next, maxRowsSel: sel };
        }
        container.appendChild(wrap);
        return { wrap, pageInput, prev, next };
    }
    // ----------------------------- Хэндлеры UI ----------------------------------
    /**
     * Подключить глобальные горячие клавиши.
     * NOTE: Горячие клавиши (Ctrl+S/F5/Esc) дергают triggerById(),
     * который кликает реальную кнопку. Если кнопка не была создана
     * (нет handler'а на момент buildUI или запрещено allows), хоткей ничем не поможет.
     * */
    attachHandlers() {
        this.keydownHandler = (ev) => {
            if (ev.ctrlKey && (ev.key === "s" || ev.key === "S")) {
                ev.preventDefault();
                this.triggerById("save");
            }
            if (ev.key === "F5") {
                ev.preventDefault();
                this.triggerById("reload");
            }
            // 2026-09-14: Escape, уже обработанный диалогом или вложенным элементом, не отменяет редактирование
            if (ev.key === "Escape" && !ev.defaultPrevented) {
                this.triggerById("cancel");
            }
        };
        document.addEventListener("keydown", this.keydownHandler);
    }
    /** Отключить горячие клавиши. */
    detachHandlers() {
        if (this.keydownHandler) {
            document.removeEventListener("keydown", this.keydownHandler);
            this.keydownHandler = undefined;
        }
    }
    /** Клик по кнопке: подтверждение (если нужно) и emit в cmdBus. */
    onClick(ev) {
        const target = ev.currentTarget;
        if (!target || target.disabled)
            return;
        const id = (target.dataset.cmd ?? "");
        // Особый кейс: тумблер фильтра (отражаем состояние в UI и шлём команду).
        if (id === "openFilter") {
            this.toggleFilter(true);
            return;
        }
        else if (id === "closeFilter") {
            this.toggleFilter(false);
            return;
        }
        // ─── Тумблер formMode (без emit в шину) ─────────────────
        if (id === "formMode") {
            this.setFormMode(!this.formModeOn);
            return;
        }
        // Обычная команда — транслируем в шину.
        this.options.cmdBus.emit(String(id), target);
    }
    // --------------------------- Локальные emit'ы -------------------------------
    /** Переход на страницу delta (navPrev/navNext) и emit команды в шину. */
    emitNav(delta) {
        const page = Math.max(1, (this.state.page ?? 1) + delta);
        this.state.page = page;
        this.ui.pagination?.pageInput && (this.ui.pagination.pageInput.value = String(page));
        const key = delta < 0 ? "navPrev" : "navNext";
        this.options.cmdBus.emit(key, this.ui.pagination?.wrap ?? this.ui.root);
    }
    /** Установить конкретную страницу (goPage) и emit команды в шину. */
    emitPage(page) {
        this.state.page = page;
        this.options.cmdBus.emit("goPage", this.ui.pagination?.wrap ?? this.ui.root);
    }
    /** Изменение «строк на странице» (setMaxRows) — emit команды в шину. */
    emitSetMaxRows() {
        this.options.cmdBus.emit("setMaxRows", this.ui.pagination?.wrap ?? this.ui.root);
    }
    // --------------------------- Правила/обновление UI --------------------------
    /** Принудительно включить/выключить режим формы и синхронизировать UI. */
    setFormMode(on) {
        this.formModeOn = !!on;
        const b = this.ui?.btns.get("formMode");
        if (!b)
            return;
        b.classList.toggle("bt-formmode", this.formModeOn);
        b.classList.toggle("bt-formmodeoff", !this.formModeOn);
    }
    /** Визуально переключить кнопку фильтра и отправить команду в шину. */
    toggleFilter(on) {
        const btn = this.ui.btns.get("openFilter");
        if (!btn)
            return;
        let execCmd;
        if (on) {
            btn.classList.add("on");
            btn.dataset.cmd = "closeFilter";
            this.state.filterOn = true;
            execCmd = 'openFilter';
        }
        else {
            execCmd = 'closeFilter';
            if (this.state.filterCount && this.state.filterCount > 0) {
                btn.dataset.cmd = "openFilter";
            }
            else {
                btn.classList.remove("on");
                btn.dataset.cmd = "openFilter";
                this.state.filterOn = false;
            }
        }
        this.options.cmdBus.emit(execCmd, btn);
    }
    /**
     * Применяет строку прав к существующим кнопкам.
     *
     * NOTE: CmdPanelWidget отрисовывает кнопки ТОЛЬКО для команд,
     * на которые обработчик был подписан в момент инициализации.
     * Поздняя подписка через cmdBus.on(...) не добавит кнопку автоматически.
     * Если нужно — либо подписывайтесь до создания панели, либо добавляйте
     * кастомные кнопки через renderCustom().
     *
     * ВАЖНО: новые кнопки сюда не подхватываются, если handler появился позже —
     * buildUI их уже не создаст.
     */
    applyAllows() {
        const allow = parseCmdAllows(this.allows);
        if (allow.hideFilter)
            this.hide("openFilter", true);
        if (allow.hideReload)
            this.hide("reload", true);
        if (allow.hideMaxRows && this.ui.pagination?.maxRowsSel) {
            this.ui.pagination.maxRowsSel.hidden = true;
        }
        this.disable("edit", !allow.edit && !allow.rowEdit);
        this.disable("insert", !allow.insert);
        this.disable("insertCopy", !allow.insert);
        this.disable("delete", !allow.del);
        this.disable("delMark", !allow.delMark);
        this.disable("insertGroup", !allow.insertGroup);
        this.disable("editGroup", !allow.insertGroup);
        this.disable("changeGroup", !(allow.rowEdit || allow.edit));
        this.disable("deleteFolder", !allow.del);
        this.disable("rowsMoveUp", !allow.move);
        this.disable("rowsMoveDown", !allow.move);
        this.disable("copy", !allow.copy);
        this.disable("cut", !allow.cut);
        this.disable("paste", !allow.paste);
        this.disable("formMode", !(allow.formEdit && allow.rowEdit));
    }
    /** Перерисовать доступность/видимость согласно текущему состоянию. */
    updateUI() {
        const s = this.state;
        // Счётчик выбранных
        const selectedCount = s.selectedCount ?? 0;
        this.ui.checkedCount.textContent = selectedCount > 0
            ? String(selectedCount)
            : this.ui.checkedCount.closest("button")
                ? "0"
                : "";
        // Loading
        if (s.mode === "loading") {
            this.setAllOff(true);
            this.disable("reload", false);
            this.ui.btns.get("reload")?.classList.add("on");
            return;
        }
        else {
            this.ui.btns.get("reload")?.classList.remove("on");
            // 1) Сначала полностью снимаем disabled/".off" со всех (кроме тех, что скрыты)
            this.setAllOff(false);
            // 2) Затем точечно применяем ограничения по правам
            this.applyAllows();
        }
        const inEdit = s.mode === "edit" || s.mode === "insert";
        const inInsert = s.mode === "insert";
        this.show("cancel", inEdit && !(inInsert && this.options.noCancelOnInsert));
        this.show("save", inEdit);
        this.show("saveClose", inEdit && !this.options.noSaveClose);
        this.show("edit", !inEdit);
        this.show("insert", !inEdit);
        this.show("insertCopy", !inEdit);
        this.disable("cancel", !inEdit);
        this.disable("save", !inEdit);
        this.disable("saveClose", !inEdit && !!this.options.noSaveClose);
        const multiOn = (s.selectedCount ?? 0) > 0 && !inEdit;
        this.disable("copy", !multiOn);
        this.disable("cut", !multiOn);
        this.disable("rowsMoveUp", !multiOn);
        this.disable("rowsMoveDown", !multiOn);
        const disableDelete = inEdit || (!multiOn && !s.hasCurrent);
        this.disable("delete", disableDelete);
        this.disable("delMark", disableDelete);
        this.disable("edit", !s.hasCurrent || inEdit);
        this.disable("insertCopy", !s.hasCurrent || inEdit);
        const filterBtn = this.ui.btns.get("openFilter");
        if (filterBtn) {
            const panelOpen = !!s.filterPanelOpen;
            const cnt = s.filterCount ?? 0;
            // 1) data-cmd зависит ТОЛЬКО от «панель открыта/закрыта»
            if (panelOpen) {
                filterBtn.dataset.cmd = "closeFilter";
                filterBtn.classList.add("on"); // горит, когда панель открыта
            }
            else {
                filterBtn.dataset.cmd = "openFilter";
                // 2) Подсветка при закрытой панели — если есть активные фильтры
                filterBtn.classList.toggle("on", cnt > 0);
            }
            // бейдж
            const badge = this.getFilterBadgeEl();
            if (badge) {
                if (cnt > 0) {
                    badge.textContent = String(cnt);
                    badge.style.display = "";
                }
                else {
                    badge.textContent = "";
                    try {
                        badge.remove();
                    }
                    catch { }
                }
            }
        }
        if (this.ui.pagination) {
            this.ui.pagination.pageInput && (this.ui.pagination.pageInput.value = String(s.page ?? 1));
            if (this.ui.pagination.prev) {
                const canPrev = (s.canPrev ?? true);
                this.ui.pagination.prev.classList.toggle("off", !canPrev);
                this.ui.pagination.prev.disabled = !canPrev;
            }
            if (this.ui.pagination.next) {
                const canNext = (s.canNext ?? true);
                this.ui.pagination.next.classList.toggle("off", !canNext);
                this.ui.pagination.next.disabled = !canNext;
            }
        }
        // ── Доп. правило для кнопки deleteFolder (поведение как раньше) ─────────
        // Базовые права уже учтены в applyAllows(), но здесь — динамика по состоянию.
        if (this.ui.btns.has("deleteFolder")) {
            // если нет права del — оставим отключённой (applyAllows уже вырубил),
            // иначе применим динамические условия:
            const allow = parseCmdAllows(this.allows);
            if (allow.del) {
                const noGroup = !s.hasGroupId;
                const disableByList = (s.selectedCount ?? 0) === 0 || !!s.filterOn || !!s.hasPagination;
                // Старые правила:
                // 1) groupID пустой → выключить
                // 2) если выбрана папка (isRowFolder) → включить
                // 3) иначе, если нет выбранных/включён фильтр/есть пагинация → выключить
                // 4) иначе → включить
                const off = noGroup ? true : (s.isRowFolder ? false : disableByList);
                this.disable("deleteFolder", off);
            }
        }
    }
    // ------------------------------ Утилиты UI ----------------------------------
    /** Показать/скрыть кнопку. */
    show(id, visible) {
        const b = this.ui.btns.get(id);
        if (!b)
            return;
        if (visible)
            DomHelpers.show(b);
        else
            DomHelpers.hide(b);
    }
    /** Принудительно скрыть/показать кнопку. */
    hide(id, hidden) {
        const b = this.ui.btns.get(id);
        if (!b)
            return;
        if (hidden)
            DomHelpers.hide(b);
        else
            DomHelpers.show(b);
    }
    /** Включить/выключить кнопку. */
    disable(id, off) {
        const b = this.ui.btns.get(id);
        if (!b)
            return;
        b.classList.toggle("off", off);
        b.disabled = off;
    }
    /** Массово выключить все (кроме reload). */
    setAllOff(off) {
        for (const b of this.ui.btns.values()) {
            const isReload = b.dataset.cmd === "reload";
            if (!isReload)
                this.disable(b.dataset.cmd || "", off);
        }
    }
    /** Программно «нажать» кнопку по id (используется хоткеями). */
    triggerById(id) {
        const btn = this.ui.btns.get(id) ?? (id === "closeFilter" ? this.ui.btns.get("openFilter") : undefined);
        if (!btn || btn.hidden || btn.disabled)
            return;
        btn.click();
    }
    /** Получение бейджа кол-ва фильтров  */
    getFilterBadgeEl() {
        const btn = this.ui.btns.get("openFilter");
        if (!btn)
            return null;
        let badge = btn.querySelector(".im-table-filterscount");
        if (!badge) {
            badge = document.createElement("span");
            badge.className = "im-table-filterscount";
            btn.appendChild(badge);
        }
        return badge;
    }
    /** Установить страницу.
     * @param page номер страницы (>=1)
     */
    setPage(page) {
        const num = Math.max(1, page);
        if (this.ui.pagination?.pageInput) {
            this.ui.pagination.pageInput.value = String(num);
        }
        // если triggerChange = false или нет pageInput — просто обновляем state и шлём команду
        this.state.page = num;
        this.options.cmdBus.emit("goPage", this.ui.pagination?.wrap ?? this.ui.root);
    }
}
/** Имя виджета */
CmdPanelWidget._alias = "CmdPanel";
/** Стандартные кнопки с подписями текущего интерфейсного словаря. */
const getStandardButtons = () => [
    // Фильтр (добавляется только если options.filterButton !== false)
    { id: "openFilter", icon: "bt-filter", title: Loc.filter },
    // Reload
    { id: "reload", icon: "bt-reload", title: Loc.reload },
    // Режим редактирования
    { id: "cancel", icon: "bt-cancel", title: Loc.cancel },
    { id: "save", icon: "bt-save", title: Loc.save },
    { id: "saveClose", icon: "bt-saveclose", title: Loc.saveChangesAndClose },
    // CRUD
    { id: "edit", icon: "bt-editrow", title: Loc.edit },
    { id: "insert", icon: "bt-insertrow", title: Loc.insert },
    { id: "insertCopy", icon: "bt-insertrow-copy", title: Loc.insertCopy },
    // Метки/удаление
    { id: "delMark", icon: "bt-delmarkrow", title: Loc.delMark },
    { id: "delete", icon: "bt-deleterow", title: Loc.deleteRow },
    // Группы/папки
    { id: "editGroup", icon: "bt-editgroup", title: Loc.editGroup },
    { id: "insertGroup", icon: "bt-insertgroup", title: Loc.insertGroup },
    { id: "changeGroup", icon: "bt-changegroup", title: Loc.changeGroup },
    { id: "deleteFolder", icon: "bt-deletefolder", title: Loc.deleteFolder },
    // Перемещения
    { id: "rowsMoveUp", icon: "bt-up", title: Loc.moveUp },
    { id: "rowsMoveDown", icon: "bt-down", title: Loc.moveDown },
    // Буфер обмена
    { id: "cut", icon: "bt-cut", title: Loc.cutSelected },
    { id: "copy", icon: "bt-copy", title: Loc.copySelected },
    { id: "paste", icon: "bt-paste", title: Loc.pasteRows },
    // Переключатель режима формы (виден только при formEdit && rowEdit)
    { id: "formMode", icon: "bt-formmode" }
];

export { CmdBus, CmdPanelWidget };
